import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useEffect } from 'react';
import { IconUser, IconUsers, IconArrowRight } from '@tabler/icons-react';

const UserTypeSelection = () => {
    const navigate = useNavigate();
    const { user, loading } = useCurrentUser();

    useEffect(() => {
        // If loading is complete and user is not authenticated, redirect to login
        if (!loading && !user) {
            navigate('/user-login');
        }
    }, [user, loading, navigate]);

    const handleSelection = (type: 'PARENT' | 'ADULT') => {
        navigate(`/create?type=${type}`);
    };

    // Check if user has an adult person - if so, redirect back to main
    const hasAdultPerson = user?.person?.some(person => person.type === 'ADULT');

    if (hasAdultPerson) {
        navigate('/main');
        return null;
    }

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Načítavam...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <img src="/img/3h.png" alt="Logo" className="w-10 h-10 mr-3" />
                            <h1 className="text-xl font-bold text-gray-900">SCVČ Tri Hôrky</h1>
                        </div>
                        <div className="text-sm text-gray-600">
                            Prihlásený: {user.email}
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl w-full space-y-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Vitajte v prihlasovacím systéme SCVČ Tri Hôrky!
                        </h2>
                        <p className="text-lg text-gray-600 mb-8">
                            Pre pokračovanie si prosím zvoľte, či ste zákonný zástupca dieťaťa alebo dospelá osoba prihlasujúca sa na kurzy.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Parent Option */}
                        <div
                            onClick={() => handleSelection('PARENT')}
                            className="bg-white rounded-lg shadow-lg p-8 cursor-pointer hover:shadow-xl transition-shadow duration-300 border-2 border-transparent hover:border-blue-500"
                        >
                            <div className="text-center">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mx-auto mb-6">
                                    <IconUsers className="h-8 w-8 text-green-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                    Zákonný zástupca
                                </h3>
                                <p className="text-gray-600 mb-6 text-base leading-relaxed">
                                    Som rodič alebo zákonný zástupca dieťaťa a chcem ho prihlásiť na kurzy alebo aktivity pre deti.
                                </p>
                                <div className="flex items-center justify-center text-green-600 font-medium">
                                    <span className="mr-2">Pokračovať</span>
                                    <IconArrowRight className="h-5 w-5" />
                                </div>
                            </div>
                        </div>

                        {/* Adult Option */}
                        <div
                            onClick={() => handleSelection('ADULT')}
                            className="bg-white rounded-lg shadow-lg p-8 cursor-pointer hover:shadow-xl transition-shadow duration-300 border-2 border-transparent hover:border-blue-500"
                        >
                            <div className="text-center">
                                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mx-auto mb-6">
                                    <IconUser className="h-8 w-8 text-blue-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                    Dospelá osoba
                                </h3>
                                <p className="text-gray-600 mb-6 text-base leading-relaxed">
                                    Som dospelá osoba a chcem sa prihlásiť na kurzy alebo aktivity pre dospelých.
                                </p>
                                <div className="flex items-center justify-center text-blue-600 font-medium">
                                    <span className="mr-2">Pokračovať</span>
                                    <IconArrowRight className="h-5 w-5" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-center text-sm text-gray-500 mt-8">
                        <p>Môžete si neskôr pridať aj ďalšie osoby podľa potreby.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserTypeSelection;
